<template>
  <div class="employee-holidays">

    <!-- ── Hero card ─────────────────────────────────────────── -->
    <v-card variant="flat" class="hol-hero">
      <div class="hol-hero__left">
        <v-avatar size="64" class="hol-hero__avatar">{{ initials }}</v-avatar>
        <div class="hol-hero__info">
          <div class="hol-hero__name">
            {{ fullName }}
            <v-chip v-if="employee.status === 1" color="success" class="ml-2">
              {{ $t("employeeHolidays.hero.active") }}
            </v-chip>
            <v-chip v-else color="neutral" class="ml-2">
              {{ $t("employeeHolidays.hero.inactive") }}
            </v-chip>
          </div>
          <div class="hol-hero__meta">
            <span v-if="employee.position">{{ employee.position }}</span>
            <span v-if="employee.position && employee.company_name" class="hol-hero__sep">·</span>
            <span v-if="employee.company_name">{{ employee.company_name }}</span>
            <span v-if="employee.manager_full_name" class="hol-hero__sep">·</span>
            <span v-if="employee.manager_full_name">{{ employee.manager_full_name }}</span>
          </div>
        </div>
      </div>
      <div class="hol-hero__actions">
        <v-btn color="primary" variant="tonal" :disabled="!employee.company_id" @click="openRequestDialog()">
          <v-icon start size="16">mdi-beach</v-icon>
          {{ $t("employeeHolidays.hero.addRequest") }}
        </v-btn>
      </div>
    </v-card>

    <!-- ── Balance + history / Calendar ──────────────────────── -->
    <v-row dense>
      <v-col cols="12" md="6" lg="7">

        <!-- Balance KPI tiles -->
        <v-card variant="flat" class="hol-card">
          <div class="hol-card__header">
            <div class="hol-card__header-left">
              <v-icon size="15" class="mr-2" style="opacity:0.62">mdi-beach</v-icon>
              <span class="hol-card__title">{{ $t("employeeHolidays.balance.title") }}</span>
            </div>
            <div class="d-flex align-center ga-1">
              <v-btn size="small" variant="text" color="primary" @click="openBalanceDialog('update')">
                <v-icon size="15" start>mdi-scale-balance</v-icon>
                {{ $t("employeeHolidays.balance.update") }}
              </v-btn>
            </div>
          </div>
          <v-divider />
          <div class="hol-balance-strip">
            <div class="hol-kpi">
              <span class="hol-kpi__value">{{ formatNumber(displayBalance.annual) }}</span>
              <span class="hol-kpi__label">{{ $t("employeeHolidays.balance.annual") }}</span>
            </div>
            <div class="hol-kpi">
              <span class="hol-kpi__value">{{ formatNumber(displayBalance.totalAccrued) }}</span>
              <span class="hol-kpi__label">{{ $t("employeeHolidays.balance.totalAccrued") }}</span>
            </div>
            <div class="hol-kpi hol-kpi--error">
              <span class="hol-kpi__value">{{ formatNumber(displayBalance.totalTaken) }}</span>
              <span class="hol-kpi__label">{{ $t("employeeHolidays.balance.taken") }}</span>
            </div>
            <div class="hol-kpi hol-kpi--success">
              <span class="hol-kpi__value">{{ formatNumber(displayBalance.remaining) }}</span>
              <span class="hol-kpi__label">{{ $t("employeeHolidays.balance.remaining") }}</span>
            </div>
          </div>
        </v-card>

        <!-- History / requests -->
        <v-card variant="flat" class="hol-card mt-3">
          <div class="hol-card__header">
            <div class="hol-card__header-left">
              <v-icon size="15" class="mr-2" style="opacity:0.62">mdi-history</v-icon>
              <span class="hol-card__title">{{ $t("employeeHolidays.history.title") }}</span>
            </div>
            <v-btn
              v-if="sortedHolidayRequests.length"
              size="x-small"
              variant="text"
              color="primary"
              @click="toggleShowAllRequests"
            >
              {{ showAllRequests ? $t("employeeHolidays.history.hideAll") : $t("employeeHolidays.history.showAll") }}
            </v-btn>
          </div>
          <v-divider />
          <div class="hol-requests">
            <template v-if="sortedHolidayRequests.length">
              <div
                v-for="request in visibleHolidayRequests"
                :key="request.id"
                :class="['hol-request-row', isPastRequest(request) ? 'hol-request-row--past' : '']"
              >
                <div class="hol-request-row__top">
                  <div class="hol-request-row__left">
                    <v-chip size="x-small" :color="statusChipColor(request.status, request.pending_action)">
                      {{ statusLabel(request.status, request.pending_action) }}
                    </v-chip>
                    <span class="hol-request-row__type">
                      {{ request.holiday_type_name || lookupHolidayType(request.holiday_type_id) || $t("employeeHolidays.history.unknownType") }}
                    </span>
                  </div>
                  <div class="hol-request-row__right">
                    <span class="hol-request-row__days">{{ formatNumber(getWorkingDays(request)) }} {{ $t("employeeHolidays.history.days") }}</span>
                    <div v-if="!companyPdfDisabled" class="d-flex align-center">
                      <v-tooltip :text="$t('employeeHolidays.history.downloadRequest')">
                        <template #activator="{ props }">
                          <v-btn icon size="x-small" variant="text" :loading="pdfDownloadLoadingId === `request-${request.id}`" v-bind="props" @click="downloadPdf(request, 'request')">
                            <v-icon size="16">mdi-download-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip v-if="request.status === 'approved'" :text="$t('employeeHolidays.history.downloadOrder')">
                        <template #activator="{ props }">
                          <v-btn icon size="x-small" variant="text" color="primary" :loading="pdfDownloadLoadingId === `order-${request.id}`" v-bind="props" @click="downloadPdf(request, 'order')">
                            <v-icon size="16">mdi-file-download-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
                <div class="hol-request-row__dates">
                  <v-icon size="12" style="opacity:0.38" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatDate(request.start_date) }} – {{ formatDate(request.end_date) }}
                </div>
                <div v-if="request.notes" class="hol-request-row__notes">{{ request.notes }}</div>
                <div v-if="!isPastRequest(request)" class="hol-request-row__actions">
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="request.pending_action === 'cancel' || (request.status === 'approved' && !canEditApprovedRequest)"
                    @click="handleEditPlaceholder(request)"
                  >
                    <v-icon start size="13">mdi-pencil-outline</v-icon>
                    {{ $t("employeeHolidays.history.edit") }}
                  </v-btn>
                </div>
              </div>
              <div v-if="hasMoreRequests" class="d-flex justify-center pa-3">
                <v-btn size="small" variant="text" color="primary" @click="showMoreRequests">
                  {{ $t("employeeHolidays.history.showMore") }}
                </v-btn>
              </div>
            </template>
            <div v-else class="hol-requests__empty">
              <v-icon size="28" style="opacity:0.25" class="mb-2">mdi-calendar-blank-outline</v-icon>
              <span>{{ $t("employeeHolidays.history.empty") }}</span>
            </div>
          </div>
        </v-card>

      </v-col>

      <!-- Calendar -->
      <v-col cols="12" md="6" lg="5">
        <v-card variant="flat" class="hol-card calendar-card">
          <div class="hol-card__header">
            <div class="hol-card__header-left">
              <v-icon size="15" class="mr-2" style="opacity:0.62">mdi-calendar-month</v-icon>
              <span class="hol-card__title">{{ $t("employeeHolidays.calendar.title") }}</span>
            </div>
          </div>
          <v-divider />
          <v-card-text class="pa-3">
            <div class="calendar-wrapper">
              <VCalendar
                class="vacation-calendar"
                type="month"
                :model-value="calendarSelectionModel"
                @update:model-value="onCalendarModelUpdate"
                :events="calendarEvents"
                :event-color="eventColorByEvent"
                color="primary"
                :weekdays="[1, 2, 3, 4, 5, 6, 0]"
              >
                <template #event="{ event }">
                  <div
                    v-if="event?.meta?.highlightKind"
                    class="calendar-day-overlay"
                    :class="`calendar-day-overlay--${event.meta.highlightKind}`"
                  ></div>
                  <v-chip class="calendar-event-chip" :color="event.color" label>
                    <span class="calendar-event-chip__dot" :class="`bg-${event.color}`"></span>
                    {{ event.title }}
                  </v-chip>
                </template>
              </VCalendar>
            </div>
            <div class="hol-legend mt-3">
              <div class="hol-legend__item">
                <span class="hol-legend__dot hol-legend__dot--approved"></span>
                {{ $t("employeeHolidays.calendar.legend.approved") }}
              </div>
              <div class="hol-legend__item">
                <span class="hol-legend__dot hol-legend__dot--pending"></span>
                {{ $t("employeeHolidays.calendar.legend.pending") }}
              </div>
              <div class="hol-legend__item">
                <span class="hol-legend__dot hol-legend__dot--non-working"></span>
                {{ $t("employeeHolidays.calendar.legend.nonWorking") }}
              </div>
              <div class="hol-legend__item">
                <span class="hol-legend__dot hol-legend__dot--short-day"></span>
                {{ $t("employeeHolidays.calendar.legend.shortDay") }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <HolidayRequestPopUp
      v-model="requestDialog"
      :loading="requestDialogLoading"
      :request-form="requestForm"
      :holiday-type-options="holidayTypeOptions"
      :status-options="statusOptions"
      :request-range-menu="requestRangeMenu"
      :range-date-model="rangeDateModel"
      :allowed-calendar-dates="allowedCalendarDates"
      :calendar-events="calendarDisplayEvents"
      :on-calendar-range-change="onCalendarRangeChange"
      :on-range-pointer-down="handleRangePointerDown"
      :on-range-field-focus="handleRangeFieldFocus"
      :on-range-keyboard-open="handleRangeKeyboardOpen"
      :on-range-control-click="handleRangeControlClick"
      :hide-status="true"
      :hide-approved="true"
      :show-cancel-action="showCancelAction"
      :cancel-action-loading="cancelRequestLoading"
      @update:request-range-menu="(value) => (requestRangeMenu = value)"
      @cancel="requestDialog = false"
      @submit="submitHolidayRequest"
      @cancel-request="cancelHolidayRequest"
    />

    <v-dialog v-model="downloadPromptDialog" max-width="420">
      <v-card>
        <v-card-title>{{ $t("employeeHolidays.download.promptTitle") }}</v-card-title>
        <v-card-text>{{ $t("employeeHolidays.download.promptBody") }}</v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="downloadPromptDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="primary"
            :loading="pdfDownloadLoadingId !== null"
            @click="confirmDownloadAfterSave"
          >
            {{ $t("app.actions.ok") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="pdfSnack.open"
      :timeout="3000"
      :color="pdfSnack.color"
      location="bottom right"
    >
      {{ pdfSnack.text }}
    </v-snackbar>

    <v-dialog v-model="balanceDialog" max-width="560px">
      <v-card>
        <v-card-title>
          {{
            balanceDialogMode === "edit"
              ? $t("employeeHolidays.balanceDialog.editTitle")
              : $t("employeeHolidays.balanceDialog.updateTitle")
          }}
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="submitBalance">
            <v-container>
              <v-row>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.annual')" type="number" v-model="balanceForm.annual_leave_days" outlined></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.carryOver')" type="number" v-model="balanceForm.carryover_days" outlined></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.accrualStart')" type="date" v-model="balanceForm.accrual_start_date" outlined></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.accrualEnd')" type="date" v-model="balanceForm.accrual_finish_date" outlined></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.totalLeave')" type="number" v-model="balanceForm.total_accrued_days" outlined readonly></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.totalTaken')" type="number" v-model="balanceForm.total_taken_days" outlined></v-text-field></v-col>
                <v-col cols="12" md="6"><v-text-field :label="$t('employeeHolidays.balanceDialog.remaining')" type="number" v-model="balanceForm.remaining_days" outlined></v-text-field></v-col>
                <v-col cols="12"><v-textarea :label="$t('employeeHolidays.balanceDialog.notes')" v-model="balanceForm.notes" rows="2" outlined></v-textarea></v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="balanceDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="balanceDialogLoading" @click="submitBalance">{{ $t("app.actions.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from "axios";
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import HolidayRequestPopUp from "./HolidayRequestPopUp.vue";
import { VCalendar } from "vuetify/labs/VCalendar";
import { computeWorkingDays as computeWorkingDaysUtil, normalizeWorkingDate } from "@/Utile/computeWorkingDays.mjs";
import { decideHighlight, buildShortDaySetFromNonWorking } from "@/Utile/calendarHighlight.mjs";
import { expandRoleAliases, normalizeRoleKey } from "@/Utile/roleHelpers.mjs";
import {
  toNumber,
  formatNumber,
  formatDate,
  formatAsIsoDate,
  normalizeDateString,
  toDateObject,
  computeAccruedUntilYearEnd,
} from "@/Utile/holidayFormatting.mjs";
import {
  deriveEmployeeId as deriveEmployeeIdHelper,
  parseEmployeeId as parseEmployeeIdHelper,
} from "@/components/dashboard/utils.mjs";

const statusMapping = {
  pending: { color: "amber" },
  approved: { color: "green" },
  rejected: { color: "error" },
  cancelled: { color: "grey" },
  draft: { color: "blue-grey" },
};

const DISPLAYED_CALENDAR_STATUSES = new Set(["pending", "approved"]);

export default defineComponent({
  name: "EmployeeHolidays",
  components: {
    HolidayRequestPopUp,
    VCalendar,
  },
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props) {
    const route = useRoute();
    const { t, locale } = useI18n();

    const parseEmployeeId = (value) => parseEmployeeIdHelper(value);

    const todayIso = formatAsIsoDate(new Date());
    const calendarSelection = ref(toDateObject(todayIso));

    const employeeId = ref(
      deriveEmployeeIdHelper({
        propId: props.employeeId,
        routeParamId: route.params.id,
        routeQueryId: route.query.id,
        storageId: localStorage.getItem("user-employee-id"),
      })
    );
    const employee = ref({});
    const holidayTypes = ref([]);
    const holidayBalance = ref(null);
    const holidayRequests = ref([]);
    const nonWorkingDays = ref([]);
    const loadingRequests = ref(false);
    const requestDialog = ref(false);
    const requestDialogLoading = ref(false);
    const requestForm = ref({
      holiday_type_id: null,
      start_date: "",
      end_date: "",
      working_days_requested: 1,
      status: "pending",
      approved: false,
      notes: "",
    });
    const requestEditingId = ref(null);
    const requestRangeMenu = ref(false);
    const requestRangeActiveEdge = ref("start");
    let stopOpenFromQueryWatcher = null;
    const isEditMode = ref(false);
    const editingRequest = ref(null);
    const editingOriginalStatus = ref("pending");
    const editingOriginalApproved = ref(false);
    const cancelRequestLoading = ref(false);
    const companyPdfDisabled = ref(false);
    const downloadPromptDialog = ref(false);
    const lastSavedRequestId = ref(null);
    const pdfDownloadLoadingId = ref(null);
    const pdfSnack = ref({
      open: false,
      color: "success",
      text: "",
    });

    const balanceDialog = ref(false);
    const balanceDialogLoading = ref(false);
    const balanceDialogMode = ref("update");
    const balanceForm = ref({
      annual_leave_days: 0,
      accrual_start_date: "",
      accrual_finish_date: "",
      carryover_days: 0,
      total_accrued_days: 0,
      total_taken_days: 0,
      remaining_days: 0,
      notes: "",
    });

    const userRole = ref(localStorage.getItem("user-role") || "");
    const storedRoleKeys = ref(
      (() => {
        try {
          const raw = localStorage.getItem("user-roles");
          const parsed = raw ? JSON.parse(raw) : [];
          return Array.isArray(parsed)
            ? parsed.map((r) => String(r?.key || r?.role || r || "").toLowerCase()).filter(Boolean)
            : [];
        } catch (e) {
          return [];
        }
      })()
    );
    const userRoleSet = computed(() => {
      const set = new Set();
      const primary = normalizeRoleKey(userRole.value);
      if (primary) {
        expandRoleAliases(primary).forEach((alias) => set.add(alias));
      }
      storedRoleKeys.value.forEach((rk) => {
        expandRoleAliases(normalizeRoleKey(rk)).forEach((alias) => set.add(alias));
      });
      return set;
    });
    const canEditApprovedRequest = computed(() =>
      ["admin", "miniadmin", "vacation-hr", "hr"].some((roleKey) => userRoleSet.value.has(roleKey))
    );

    const holidayTypeMap = computed(() => {
      const map = new Map();
      holidayTypes.value.forEach((type) => {
        map.set(Number(type.id), type);
      });
      return map;
    });

    const initials = computed(() => {
      const fn = employee.value.first_name?.[0] || "";
      const ln = employee.value.last_name?.[0] || "";
      return `${fn}${ln}`.toUpperCase() || "?";
    });

    const fullName = computed(() => `${employee.value.first_name || ''} ${employee.value.last_name || ''}`.trim());

    const statusOptions = computed(() =>
      Object.entries(statusMapping).map(([value, meta]) => ({ value, title: meta.label }))
    );

    const holidayTypeOptions = computed(() =>
      holidayTypes.value.map((type) => ({
        title: `${type.name_ro}${Number(type.is_paid) ? ' • plătit' : ''}`,
        value: type.id,
      }))
    );

    const currentHolidayType = computed(() =>
      holidayTypeMap.value.get(Number(requestForm.value.holiday_type_id)) || null
    );

    const ANNUAL_LEAVE_CODES = new Set(["annual_leave"]);
    const ANNUAL_LEAVE_NAMES = new Set([
      "Concediu anual de odihnă",
      "Concediu anual de odihnă - plătit",
      "Concediu anual de odihnă • plătit",
    ]);

    const isAnnualLeaveType = (type) => {
      if (!type) return false;
      if (type.code && ANNUAL_LEAVE_CODES.has(type.code)) return true;
      if (type.name_ro && ANNUAL_LEAVE_NAMES.has(type.name_ro)) return true;
      return false;
    };

    const currentTypeReducesBalance = computed(() => isAnnualLeaveType(currentHolidayType.value));

    const holidayTypeMapById = computed(() => holidayTypeMap.value);

    const sortedHolidayRequests = computed(() => {
      const now = new Date();
      const upcoming = [];
      const past = [];

      holidayRequests.value.forEach((request) => {
        const end = new Date(request.end_date || request.start_date || request.created_at || Date.now());
        const bucket = end >= now ? upcoming : past;
        bucket.push(request);
      });

      upcoming.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return aDate - bDate;
      });

      past.sort((a, b) => {
        const aDate = new Date(a.start_date || a.created_at || 0).getTime();
        const bDate = new Date(b.start_date || b.created_at || 0).getTime();
        return bDate - aDate;
      });

      return [...upcoming, ...past];
    });

    const INITIAL_VISIBLE_REQUESTS = 4;
    const LOAD_MORE_STEP = 10;
    const visibleRequestCount = ref(INITIAL_VISIBLE_REQUESTS);
    const showAllRequests = ref(false);
    const visibleHolidayRequests = computed(() =>
      showAllRequests.value
        ? sortedHolidayRequests.value
        : sortedHolidayRequests.value.slice(0, Math.max(0, visibleRequestCount.value))
    );
    const hasMoreRequests = computed(
      () => !showAllRequests.value && sortedHolidayRequests.value.length > visibleRequestCount.value
    );
    const showCancelAction = computed(() => {
      if (!isEditMode.value || !editingRequest.value) return false;
      if (editingRequest.value.pending_action === "cancel") return false;
      if (isPastRequest(editingRequest.value)) return false;
      return ["approved", "pending"].includes(editingOriginalStatus.value);
    });
    function showMoreRequests() {
      if (showAllRequests.value) return;
      visibleRequestCount.value += LOAD_MORE_STEP;
    }

    const toggleShowAllRequests = () => {
      showAllRequests.value = !showAllRequests.value;
      if (!showAllRequests.value) {
        visibleRequestCount.value = INITIAL_VISIBLE_REQUESTS;
      }
    };

    const nonWorkingSet = computed(() => {
      const set = new Set();
      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        if (iso) {
          set.add(iso);
        }
      });
      return set;
    });

    const computeDaySetForStatus = (status) => {
      const set = new Set();
      const editingId = requestEditingId.value ? Number(requestEditingId.value) : null;

      holidayRequests.value.forEach((req) => {
        if (req.status !== status) return;
        if (!isAnnualLeaveType(holidayTypeMap.value.get(Number(req.holiday_type_id)))) return;
        if (editingId && Number(req.id) === editingId) return;

        const start = toDateObject(req.start_date);
        const end = toDateObject(req.end_date);
        if (!start || !end) return;

        const cursor = new Date(start);
        while (cursor <= end) {
          const iso = formatAsIsoDate(cursor);
          if (iso) {
            set.add(iso);
          }
          cursor.setDate(cursor.getDate() + 1);
        }
      });

      return set;
    };

    const blockedDaysSet = computed(() => computeDaySetForStatus("approved"));
    const pendingDaysSet = computed(() => computeDaySetForStatus("pending"));
    const calendarDisplayEvents = computed(() => {
      if (!currentTypeReducesBalance.value) return [];
      const map = new Map();

      nonWorkingSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(`non-working-${iso}`, { date, color: "neutral" });
        }
      });

      pendingDaysSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(`pending-${iso}`, { date, color: "warning" });
        }
      });

      blockedDaysSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(`holiday-${iso}`, { date, color: "error" });
        }
      });

      return Array.from(map.values());
    });

    const allowedCalendarDates = (value) => {
      const iso = normalizeDateString(value);
      if (!iso) return false;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && iso > finish) return false;
      if (!currentTypeReducesBalance.value) return true;
      if (nonWorkingSet.value.has(iso)) {
        if (requestEditingId.value) {
          const editing = holidayRequests.value.find((req) => Number(req.id) === Number(requestEditingId.value));
          if (editing) {
            const startIso = normalizeDateString(editing.start_date);
            const endIso = normalizeDateString(editing.end_date);
            if (startIso && endIso && startIso <= iso && iso <= endIso) {
              return true;
            }
          }
        }
        return false;
      }
      if (pendingDaysSet.value.has(iso)) {
        if (requestEditingId.value) {
          const editing = holidayRequests.value.find((req) => Number(req.id) === Number(requestEditingId.value));
          if (editing && editing.status === "pending") {
            const startIso = normalizeDateString(editing.start_date);
            const endIso = normalizeDateString(editing.end_date);
            if (startIso && endIso && startIso <= iso && iso <= endIso) {
              return true;
            }
          }
        }
        return false;
      }
      return !blockedDaysSet.value.has(iso);
    };

    const rangeDateModel = computed(() => {
      const start = toDateObject(requestForm.value.start_date);
      const end = toDateObject(requestForm.value.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }

      if (start) {
        return [start];
      }

      return [];
    });

    const normalizeRangeSelection = (value) => {
      if (!value) {
        return { start: "", end: "", hasStart: false, hasEnd: false };
      }

      let startValue = null;
      let endValue = null;

      if (Array.isArray(value)) {
        [startValue, endValue] = value;
      } else if (value instanceof Date || typeof value === "string") {
        startValue = value;
      } else if (typeof value === "object") {
        if ("start" in value || "end" in value) {
          startValue = value.start ?? null;
          endValue = value.end ?? null;
        }
      }

      const startIso = normalizeDateString(startValue);
      const endIso = normalizeDateString(endValue);

      if (startIso && endIso) {
        if (startIso <= endIso) {
          return { start: startIso, end: endIso, hasStart: true, hasEnd: true };
        }
        return { start: endIso, end: startIso, hasStart: true, hasEnd: true };
      }

      return {
        start: startIso || "",
        end: endIso || "",
        hasStart: Boolean(startIso),
        hasEnd: Boolean(endIso),
      };
    };

    const calendarEvents = computed(() => {
      // Build day-wise buckets so we can decide a single highlight per day
      const days = new Map(); // iso -> { events: [], kinds: Set }
      const nonWorkingIsoSet = new Set();

      const ensureDay = (iso) => {
        const bucket = days.get(iso) || { events: [], kinds: new Set() };
        if (!days.has(iso)) days.set(iso, bucket);
        return bucket;
      };

      // Non-working days (kind: non-working)
      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        const date = toDateObject(iso);
        if (!date) return;
        nonWorkingIsoSet.add(iso);
        const ev = {
          start: date,
          end: date,
          color: 'neutral',
          title: entry?.name || 'Zi nelucrătoare',
          allDay: true,
          meta: { kind: 'non-working', iso, highlightKind: null },
        };
        const b = ensureDay(iso);
        b.events.push(ev);
        b.kinds.add('non-working');
      });

      // Short days (day before a non-working day)
      const shortDaySet = buildShortDaySetFromNonWorking(nonWorkingDays.value);
      shortDaySet.forEach((iso) => {
        if (nonWorkingIsoSet.has(iso)) return;
        const date = toDateObject(iso);
        if (!date) return;
        const ev = {
          start: date,
          end: date,
          color: 'info',
          title: 'Zi scurtă (pre-sărbătoare)',
          allDay: true,
          meta: { kind: 'short-day', iso, highlightKind: null },
        };
        const b = ensureDay(iso);
        b.events.push(ev);
        b.kinds.add('short-day');
      });

      // Requests, only pending/approved
      holidayRequests.value
        .filter((req) => DISPLAYED_CALENDAR_STATUSES.has(req.status))
        .forEach((req) => {
          const startDate = toDateObject(req.start_date);
          const endDate = toDateObject(req.end_date || req.start_date) || startDate;
          if (!startDate) return;

          const statusColor = statusChipColor(req.status, req.pending_action);
          const themedColor = statusColor === 'green'
            ? 'success'
            : statusColor === 'amber'
              ? 'warning'
              : statusColor === 'error'
                ? 'error'
                : statusColor === 'blue-grey' || statusColor === 'grey'
                  ? 'neutral'
                  : statusColor || 'primary';
          const kind = req.status === 'pending' ? 'pending' : 'approved';
          const title =
            req.holiday_type_name ||
            lookupHolidayType(req.holiday_type_id) ||
            statusLabel(req.status, req.pending_action);

          const cursor = new Date(startDate);
          while (cursor <= endDate) {
            const iso = formatAsIsoDate(cursor);
            const ev = {
              start: new Date(cursor),
              end: new Date(cursor),
              color: themedColor,
              title,
              allDay: true,
              meta: { kind, iso, highlightKind: null },
            };
            const b = ensureDay(iso);
            b.events.push(ev);
            b.kinds.add(kind);
            cursor.setDate(cursor.getDate() + 1);
          }
        });

      // Decide highlight by priority: non-working > pending > approved
      const result = [];
      days.forEach((bucket) => {
        const highlight = decideHighlight(bucket.kinds);
        // mark the first event as highlight carrier
        if (bucket.events.length) bucket.events[0].meta.highlightKind = highlight;
        result.push(...bucket.events);
      });

      return result;
    });


    const displayBalance = computed(() => {
      const annual = toNumber(holidayBalance.value?.annual_leave_days, 0);
      const carry = toNumber(holidayBalance.value?.carryover_days, 0);
      const accrualStartRaw = holidayBalance.value?.accrual_start_date;
      const totalAccrued = computeAccruedUntilYearEnd(
        annual,
        carry,
        accrualStartRaw,
        holidayBalance.value?.accrual_finish_date || null
      );
      const totalTaken = totalTakenDisplay.value;
      const remaining = Number((totalAccrued - totalTaken).toFixed(2));
      return {
        annual,
        totalAccrued,
        totalTaken,
        remaining,
      };
    });

    const totalTakenDisplay = computed(() => {
      const fromBalance = toNumber(holidayBalance.value?.total_taken_days, 0);
      if (fromBalance > 0) return fromBalance;
      return holidayRequests.value
        .filter((req) => req.status === "approved")
        .filter((req) => isAnnualLeaveType(holidayTypeMap.value.get(Number(req.holiday_type_id))))
        .reduce((sum, req) => sum + getWorkingDays(req), 0);
    });

    function getWorkingDays(request) {
      if (!request) return 0;
      const recorded = toNumber(request.working_days_requested, 0);
      if (recorded) return recorded;
      const start = request.start_date ? formatDate(request.start_date) || request.start_date : "";
      const end = request.end_date ? formatDate(request.end_date) || request.end_date : "";
      return computeWorkingDays(start, end);
    }

    const computeWorkingDays = (start, end) => {
      return computeWorkingDaysUtil(start, end, {
        nonWorkingDates: nonWorkingSet.value,
      });
    };

    function applyWorkingDays() {
      if (!requestForm.value.start_date || !requestForm.value.end_date) return;
      const days = computeWorkingDays(
        normalizeWorkingDate(requestForm.value.start_date),
        normalizeWorkingDate(requestForm.value.end_date)
      );
      requestForm.value.working_days_requested = Number(days.toFixed(2));
    }

    const loadEmployeeContext = async () => {
      let parsedId = parseEmployeeId(employeeId.value);

      if (!parsedId) {
        parsedId = await fetchEmployeeIdFromProfile();
      }

      if (!parsedId) {
        resetEmployeeState();
        return;
      }

      employeeId.value = parsedId;

      await Promise.all([fetchEmployee(), fetchHolidayTypes()]);
      await fetchHolidayData();
    };

    const handleEmployeeIdUpdate = async (nextValue) => {
      const parsedId = parseEmployeeId(nextValue);
      if (parsedId === employeeId.value) {
        return;
      }

      employeeId.value = parsedId;
      resetEmployeeState();

      if (parsedId) {
        await loadEmployeeContext();
      }
    };

    const fetchEmployeeIdFromProfile = async () => {
      try {
        const { data } = await axios.get("/me");
        const parsedId = parseEmployeeId(data?.employee_id);

        if (parsedId) {
          employeeId.value = parsedId;
          localStorage.setItem("user-employee-id", String(parsedId));
        } else {
          employeeId.value = null;
          localStorage.removeItem("user-employee-id");
        }

        return parsedId;
      } catch (error) {
        console.error("Eroare la obținerea profilului utilizatorului:", error);
        return null;
      }
    };

    async function fetchEmployee() {
      let parsedId = parseEmployeeId(employeeId.value);
      if (!parsedId) {
        parsedId = await fetchEmployeeIdFromProfile();
      }
      if (!parsedId) {
        employee.value = {};
        return;
      }

      try {
        const response = await axios.get(`/candidati/employees/${parsedId}`);
        employee.value = response.data || {};
      } catch (error) {
        console.error("Eroare la obținerea datelor angajatului:", error);
      }
    }

    async function fetchHolidayTypes() {
      try {
        const { data } = await axios.get("/holidays/types");
        holidayTypes.value = data || [];
      } catch (error) {
        console.error("Eroare fetchHolidayTypes:", error);
        holidayTypes.value = [];
      }
    }

    async function fetchNonWorkingDays(companyId) {
      if (!companyId) {
        nonWorkingDays.value = [];
        return;
      }

      const now = new Date();
      let minDate = new Date(now.getFullYear(), 0, 1);
      let maxDate = new Date(now.getFullYear() + 1, 11, 31);

      holidayRequests.value.forEach((req) => {
        const startIso = normalizeDateString(req.start_date);
        const endIso = normalizeDateString(req.end_date);
        const startDate = startIso ? toDateObject(startIso) : null;
        const endDate = endIso ? toDateObject(endIso) : null;
        if (startDate && startDate < minDate) {
          minDate = new Date(startDate);
        }
        if (endDate && endDate > maxDate) {
          maxDate = new Date(endDate);
        }
      });

      try {
        const { data } = await axios.get("/holidays/non-working-days", {
          params: {
            company_id: companyId,
            start_date: formatAsIsoDate(minDate),
            end_date: formatAsIsoDate(maxDate),
          },
        });

        nonWorkingDays.value = Array.isArray(data?.occurrences)
          ? data.occurrences
              .map((occ) => ({
                id: Number(occ.id),
                occurrence_date: normalizeDateString(occ.occurrence_date || occ.date),
                name: occ.name || "",
              }))
              .filter((occ) => occ.occurrence_date)
          : [];
      } catch (error) {
        console.error("Eroare fetchNonWorkingDays:", error);
        nonWorkingDays.value = [];
      }
    }

    async function fetchHolidayData() {
      if (!employee.value.company_id) {
        calendarSelection.value = toDateObject(todayIso);
        return;
      }
      loadingRequests.value = true;
      try {
        const [balanceRes, requestsRes] = await Promise.all([
          axios.get(`/holidays/balances`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
          axios.get(`/holidays/requests`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
        ]);
        holidayBalance.value = balanceRes.data?.[0] || null;
        holidayRequests.value = requestsRes.data || [];
        companyPdfDisabled.value = Boolean(requestsRes.data?.[0]?.company_pdfrequest);
        await fetchNonWorkingDays(employee.value.company_id);
      } catch (error) {
        console.error("Eroare fetchHolidayData:", error);
      } finally {
        loadingRequests.value = false;
      }
    }

    function resetEmployeeState() {
      employee.value = {};
      holidayBalance.value = null;
      holidayRequests.value = [];
      nonWorkingDays.value = [];
      requestDialog.value = false;
      requestRangeMenu.value = false;
      requestForm.value = {
        holiday_type_id: null,
        start_date: "",
        end_date: "",
        working_days_requested: 1,
        status: "pending",
        approved: false,
        notes: "",
      };
      requestEditingId.value = null;
      balanceForm.value = {
        annual_leave_days: 0,
        accrual_start_date: "",
        accrual_finish_date: "",
        carryover_days: 0,
        total_accrued_days: 0,
        total_taken_days: 0,
        remaining_days: 0,
        notes: "",
      };
    }

    function openRequestDialog(request = null) {
      if (!employee.value?.company_id) return;

      requestRangeMenu.value = false;
      requestRangeActiveEdge.value = "start";

      if (request) {
        isEditMode.value = true;
        editingRequest.value = request;
        requestEditingId.value = request.id;
        editingOriginalStatus.value = request.status;
        editingOriginalApproved.value = Number(request.approved) === 1;
        requestForm.value = {
          holiday_type_id: request.holiday_type_id,
          start_date: formatDate(request.start_date) || request.start_date || "",
          end_date: formatDate(request.end_date) || request.end_date || "",
          working_days_requested: Number(request.working_days_requested || 1),
          status: request.status,
          approved: Number(request.approved) === 1,
          notes: request.notes || "",
        };
      } else {
        isEditMode.value = false;
        editingRequest.value = null;
        requestEditingId.value = null;
        editingOriginalStatus.value = "pending";
        editingOriginalApproved.value = false;

        const defaultTypeId = holidayTypeOptions.value[0]?.value || null;

        requestForm.value = {
          holiday_type_id: defaultTypeId,
          start_date: "",
          end_date: "",
          working_days_requested: 1,
          status: "pending",
          approved: false,
          notes: "",
        };

        let today = new Date();
        let formatted = formatAsIsoDate(today);
        const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
        if (finish) {
          const finishDate = toDateObject(finish);
          if (!finishDate || finishDate < today) {
            alert(`Nu poți crea cereri după ${finish}.`);
            return;
          }
        }
        let guard = 0;
        while (formatted && hasApprovedOverlap(formatted, formatted) && guard < 365) {
          today.setDate(today.getDate() + 1);
          formatted = formatAsIsoDate(today);
          guard += 1;
        }

        if (formatted) {
          requestForm.value.start_date = formatted;
          requestForm.value.end_date = formatted;
        }
      }

      applyWorkingDays();
      requestDialog.value = true;
    }

    function stopPendingQueryWatcher() {
      if (typeof stopOpenFromQueryWatcher === "function") {
        stopOpenFromQueryWatcher();
        stopOpenFromQueryWatcher = null;
      }
    }

    function triggerOpenRequestFromQuery() {
      const flag = route.query?.openHolidayRequest;
      if (!flag || requestDialog.value) return;

      const ready =
        Array.isArray(holidayTypeOptions.value) &&
        holidayTypeOptions.value.length > 0 &&
        Boolean(employee.value?.company_id);

      if (ready) {
        openRequestDialog();
        return;
      }

      if (!stopOpenFromQueryWatcher) {
        stopOpenFromQueryWatcher = watch(
          () => ({
            optionsReady:
              Array.isArray(holidayTypeOptions.value) && holidayTypeOptions.value.length > 0,
            companyReady: Boolean(employee.value?.company_id),
          }),
          ({ optionsReady, companyReady }) => {
            if (optionsReady && companyReady) {
              openRequestDialog();
              stopPendingQueryWatcher();
            }
          },
          { immediate: true }
        );
      }
    }

    async function submitHolidayRequest() {
      if (!employee.value.company_id) {
        alert(t("employeeHolidays.errors.unknownCompany"));
        return;
      }
      const start = requestForm.value.start_date;
      const end = requestForm.value.end_date;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && end && end > finish) {
        alert(t("employeeHolidays.errors.afterFinish", { date: finish }));
        return;
      }
      if (hasApprovedOverlap(start, end, requestEditingId.value)) {
        alert(t("employeeHolidays.errors.overlap"));
        return;
      }
      requestDialogLoading.value = true;
      applyWorkingDays();
      const payload = {
        company_id: employee.value.company_id,
        employee_id: employeeId.value,
        holiday_type_id: requestForm.value.holiday_type_id,
        start_date: requestForm.value.start_date,
        end_date: requestForm.value.end_date,
        working_days_requested: requestForm.value.working_days_requested,
        status: isEditMode.value ? editingOriginalStatus.value : "pending",
        approved: isEditMode.value ? editingOriginalApproved.value : false,
        notes: requestForm.value.notes,
        requested_by_employee_id: employeeId.value,
      };

      try {
        let savedId = requestEditingId.value || null;
        if (requestEditingId.value) {
          await axios.put(`/holidays/requests/${requestEditingId.value}`, payload);
        } else {
          const { data } = await axios.post("/holidays/requests", payload);
          savedId = data?.id || null;
        }
        requestDialog.value = false;
        await fetchHolidayData();
        if (!companyPdfDisabled.value && savedId) {
          lastSavedRequestId.value = savedId;
          downloadPromptDialog.value = true;
        }
      } catch (error) {
        console.error("Eroare submitHolidayRequest:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.errors.saveRequest");
        alert(message);
      } finally {
        requestDialogLoading.value = false;
      }
    }

    async function cancelHolidayRequest() {
      if (!isEditMode.value || !editingRequest.value) {
        return;
      }

      if (editingRequest.value.pending_action === "cancel") {
        alert(t("employeeHolidays.errors.pendingCancellation"));
        return;
      }

      const reason = (requestForm.value.notes || "").trim();
      if (!reason) {
        alert(t("employeeHolidays.errors.cancellationReason"));
        return;
      }

      cancelRequestLoading.value = true;
      try {
        await axios.post(`/holidays/requests/${editingRequest.value.id}/cancel-request`, {
          notes: reason,
        });
        requestDialog.value = false;
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare cancelHolidayRequest:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.errors.cancelFailed");
        alert(message);
      } finally {
        cancelRequestLoading.value = false;
      }
    }

    async function downloadPdf(request, kind = "request") {
      if (companyPdfDisabled.value) {
        alert(t("employeeHolidays.errors.pdfDisabled"));
        return;
      }
      const requestId = typeof request === "object" ? request?.id : request;
      if (!requestId) return;
      pdfDownloadLoadingId.value = `${kind}-${requestId}`;
      try {
        const url =
          kind === "order"
            ? `/holidays/requests/${requestId}/pdf-order`
            : `/holidays/requests/${requestId}/pdf-request`;
        const { data } = await axios.get(url, { responseType: "blob" });
        const blob = new Blob([data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        const base =
          kind === "order"
            ? t("employeeHolidays.download.orderFile")
            : t("employeeHolidays.download.requestFile");
        link.download = `${base}-${requestId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        pdfSnack.value = {
          open: true,
          color: "success",
          text: t("employeeHolidays.download.started"),
        };
      } catch (error) {
        console.error("Eroare download PDF:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.download.error");
        pdfSnack.value = {
          open: true,
          color: "error",
          text: message,
        };
      } finally {
        pdfDownloadLoadingId.value = null;
      }
    }

    const confirmDownloadAfterSave = async () => {
      downloadPromptDialog.value = false;
      if (lastSavedRequestId.value) {
        await downloadPdf(lastSavedRequestId.value, "request");
      }
    };

    function openBalanceDialog(mode = "update") {
      balanceDialogMode.value = mode;
      if (holidayBalance.value) {
        balanceForm.value = {
          annual_leave_days: holidayBalance.value.annual_leave_days || 0,
          accrual_start_date: holidayBalance.value.accrual_start_date || "",
          accrual_finish_date: holidayBalance.value.accrual_finish_date || "",
          carryover_days: holidayBalance.value.carryover_days || 0,
          total_accrued_days: holidayBalance.value.total_accrued_days || 0,
          total_taken_days: holidayBalance.value.total_taken_days || 0,
          remaining_days: holidayBalance.value.remaining_days || 0,
          notes: holidayBalance.value.notes || "",
        };
      }
      balanceDialog.value = true;
    }

    async function submitBalance() {
      if (!employee.value.company_id) {
        alert(t("employeeHolidays.errors.unknownCompany"));
        return;
      }
      balanceDialogLoading.value = true;
      try {
        const payload = {
          company_id: employee.value.company_id,
          employee_id: employeeId.value,
          annual_leave_days: Number(balanceForm.value.annual_leave_days) || 0,
          accrual_start_date: balanceForm.value.accrual_start_date,
          accrual_finish_date: balanceForm.value.accrual_finish_date || null,
          carryover_days: Number(balanceForm.value.carryover_days) || 0,
          total_accrued_days: Number(balanceForm.value.total_accrued_days) || 0,
          total_taken_days: Number(balanceForm.value.total_taken_days) || 0,
          remaining_days: Number(balanceForm.value.remaining_days) || 0,
          notes: balanceForm.value.notes,
          skip_recalc: balanceDialogMode.value === 'edit' ? 1 : 0,
        };
        await axios.post("/holidays/balances", payload);
        balanceDialog.value = false;
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare submitBalance:", error);
        alert(t("employeeHolidays.errors.saveBalance"));
      } finally {
        balanceDialogLoading.value = false;
        balanceForm.value._editMode = false;
      }
    }

    function hasApprovedOverlap(startValue, endValue, excludeId = null) {
      if (!startValue || !endValue) return false;
      if (!currentTypeReducesBalance.value) return false;
      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
      if (endDate < startDate) return false;

      return holidayRequests.value.some((req) => {
        if (excludeId && Number(req.id) === Number(excludeId)) return false;
        if (req.status !== "approved") return false;
        const type = holidayTypeMap.value.get(Number(req.holiday_type_id));
        if (!isAnnualLeaveType(type)) return false;
        const reqStart = new Date(req.start_date);
        const reqEnd = new Date(req.end_date);
        if (Number.isNaN(reqStart.getTime()) || Number.isNaN(reqEnd.getTime())) return false;
        return startDate <= reqEnd && endDate >= reqStart;
      });
    }

    function handleRangePointerDown(edge) {
      requestRangeActiveEdge.value = edge;
    }

    function handleRangeFieldFocus(edge) {
      requestRangeActiveEdge.value = edge;
    }

    function handleRangeKeyboardOpen(edge) {
      requestRangeActiveEdge.value = edge;
      requestRangeMenu.value = true;
    }

    function handleRangeControlClick(edge, event, menuProps) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      requestRangeActiveEdge.value = edge;
      if (menuProps) {
        menuProps.onClick?.(event);
      }
    }

    function onCalendarRangeChange(value) {
      const selection = normalizeRangeSelection(value);
      const targetEdge = requestRangeActiveEdge.value || "start";
      const currentStart = requestForm.value.start_date;
      const currentEnd = requestForm.value.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            requestForm.value.start_date = candidate;
            requestForm.value.end_date = currentStart;
          } else {
            requestForm.value.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              requestForm.value.start_date = selection.start;
            }
          }

          if (requestForm.value.start_date && requestForm.value.end_date) {
            requestRangeMenu.value = false;
          }
        }

        applyWorkingDays();
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            requestForm.value.start_date = nextStart;
            requestForm.value.end_date = nextEnd;
          } else {
            requestForm.value.start_date = nextEnd;
            requestForm.value.end_date = nextStart;
          }
          requestRangeMenu.value = false;
        } else {
          requestForm.value.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            requestForm.value.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            requestRangeActiveEdge.value = "end";
          }
        }
      }

      applyWorkingDays();
    }

    function lookupHolidayType(id) {
      const type = holidayTypeMapById.value.get(Number(id));
      return type ? type.name_ro : null;
    }

    function statusLabel(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return t("employeeHolidays.status.pendingCancel");
      }
      const key = status
        ? `employeeHolidays.status.${status}`
        : null;
      return (key && t(key)) || status;
    }

    function statusChipColor(status, pendingAction = null) {
      if (status === "pending" && pendingAction === "cancel") {
        return "warning";
      }
      return statusMapping[status]?.color || "grey";
    }

    function isPastRequest(request) {
      const end = new Date(request.end_date || request.start_date || request.created_at || Date.now());
      const today = new Date();
      end.setHours(23, 59, 59, 999);
      return end < today;
    }

    function handleEditPlaceholder(request) {
      if (!request || request.pending_action === "cancel") return;
      if (request.status === "approved" && !canEditApprovedRequest.value) return;
      openRequestDialog(request);
    }

    const calendarSelectionModel = computed(() =>
      calendarSelection.value ? [calendarSelection.value] : []
    );

    const eventMap = computed(() => {
      const m = new Map();
      calendarDisplayEvents.value.forEach((event) => {
        const iso = normalizeDateString(event?.date);
        if (!iso) return;
        const colors = m.get(iso) || [];
        colors.push(event.color);
        m.set(iso, colors);
      });
      return m;
    });

    const eventColorByDate = (value) => {
      const iso = normalizeDateString(value);
      const list = eventMap.value.get(iso) || [];
      return list.length ? list : [];
    };

    const eventColorByEvent = (event) => event?.color || "primary";

    const onCalendarModelUpdate = (value) => {
      if (Array.isArray(value)) {
        calendarSelection.value = value[0] || toDateObject(todayIso);
      } else if (value) {
        calendarSelection.value = value instanceof Date ? value : toDateObject(value);
      }
    };

    onMounted(async () => {
      await loadEmployeeContext();
      triggerOpenRequestFromQuery();
    });

    watch(
      () => props.employeeId,
      async (nextValue, previousValue) => {
        if (nextValue === previousValue) return;
        await handleEmployeeIdUpdate(nextValue);
      }
    );

    watch(
      () => [route.params.id, route.query.id],
      async ([paramId, queryId], [oldParamId, oldQueryId]) => {
        const nextId = paramId ?? queryId;
        const prevId = oldParamId ?? oldQueryId;
        if (nextId === prevId) return;
        await handleEmployeeIdUpdate(nextId);
      }
    );

    watch(
      () => route.query?.openHolidayRequest,
      (value, previous) => {
        if (value === previous) return;
        if (value) {
          triggerOpenRequestFromQuery();
        } else {
          stopPendingQueryWatcher();
        }
      }
    );

    watch(
      () => requestForm.value.holiday_type_id,
      () => {
        applyWorkingDays();
      }
    );

    watch(
      () => [requestForm.value.start_date, requestForm.value.end_date],
      () => {
        applyWorkingDays();
      }
    );

    watch(requestDialog, (open) => {
      if (!open) {
        isEditMode.value = false;
        editingRequest.value = null;
        requestEditingId.value = null;
        editingOriginalStatus.value = "pending";
        editingOriginalApproved.value = false;
        cancelRequestLoading.value = false;
      }
    });

    watch(
      sortedHolidayRequests,
      () => {
        // Reset pagination when the underlying list changes (e.g., refetch)
        if (!showAllRequests.value) {
          visibleRequestCount.value = INITIAL_VISIBLE_REQUESTS;
        }
      },
      { immediate: true }
    );

    return {
      employeeId,
      employee,
      holidayBalance,
      holidayRequests,
      loadingRequests,
      requestDialog,
      requestDialogLoading,
      requestForm,
      requestRangeMenu,
      rangeDateModel,
      allowedCalendarDates,
      calendarDisplayEvents,
      calendarEvents,
      eventColorByDate,
      eventColorByEvent,
      onCalendarModelUpdate,
      holidayTypeOptions,
      statusOptions,
      displayBalance,
      formatNumber,
      formatDate,
      statusLabel,
      statusChipColor,
      canEditApprovedRequest,
      showCancelAction,
      cancelRequestLoading,
      lookupHolidayType,
      getWorkingDays,
      initials,
      fullName,
      openRequestDialog,
      submitHolidayRequest,
      cancelHolidayRequest,
      onCalendarRangeChange,
      handleRangePointerDown,
      handleRangeFieldFocus,
      handleRangeKeyboardOpen,
      handleRangeControlClick,
      balanceDialog,
      balanceDialogMode,
      balanceForm,
      openBalanceDialog,
      submitBalance,
      sortedHolidayRequests,
      visibleHolidayRequests,
      hasMoreRequests,
      showMoreRequests,
      showAllRequests,
      toggleShowAllRequests,
      isPastRequest,
      handleEditPlaceholder,
      todayIso,
      calendarSelection,
      calendarSelectionModel,
      companyPdfDisabled,
      downloadPdf,
      downloadPromptDialog,
      confirmDownloadAfterSave,
      pdfDownloadLoadingId,
      pdfSnack,
    };
  },
});
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   EmployeeHolidays — design tokens aligned to design.md
   ═══════════════════════════════════════════════════════════ */

/* ── Page container ──────────────────────────────────────── */
.employee-holidays {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Hero card ───────────────────────────────────────────── */
.hol-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  border-radius: var(--app-radius-base) !important;
  border-left: 4px solid rgb(var(--v-theme-tertiary)) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}
.hol-hero__left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
}
.hol-hero__avatar {
  flex-shrink: 0;
  background: rgba(var(--v-theme-tertiary), 0.12) !important;
  color: rgb(var(--v-theme-tertiary)) !important;
  font-size: 22px;
  font-weight: 700;
}
.hol-hero__info {
  min-width: 0;
}
.hol-hero__name {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--app-text-strong);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.hol-hero__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--app-text-default);
  margin-top: 4px;
}
.hol-hero__sep {
  opacity: 0.45;
}
.hol-hero__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

/* ── Generic content card ────────────────────────────────── */
.hol-card {
  border-radius: var(--app-radius-base) !important;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}
.hol-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 8px;
}
.hol-card__header-left {
  display: flex;
  align-items: center;
}
.hol-card__title {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--app-text-default);
}

/* ── Balance KPI strip ───────────────────────────────────── */
.hol-balance-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
.hol-kpi {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  background: rgb(var(--v-theme-surface));
  text-align: center;
  gap: 4px;
}
.hol-kpi__value {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
  color: var(--app-text-strong);
}
.hol-kpi--error .hol-kpi__value {
  color: rgb(var(--v-theme-error));
}
.hol-kpi--success .hol-kpi__value {
  color: rgb(var(--v-theme-success));
}
.hol-kpi__label {
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--app-text-default);
  opacity: 0.75;
}

/* ── Requests list ───────────────────────────────────────── */
.hol-requests {
  display: flex;
  flex-direction: column;
}
.hol-request-row {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  transition: background 0.15s;
}
.hol-request-row:last-child {
  border-bottom: none;
}
.hol-request-row:hover {
  background: rgba(var(--v-theme-on-surface), 0.03);
}
.hol-request-row--past {
  opacity: 0.55;
}
.hol-request-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.hol-request-row__left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.hol-request-row__right {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.hol-request-row__type {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--app-text-strong);
}
.hol-request-row__days {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--app-text-default);
  white-space: nowrap;
}
.hol-request-row__dates {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: var(--app-text-default);
  margin-top: 4px;
  opacity: 0.8;
}
.hol-request-row__notes {
  font-size: 0.78rem;
  color: var(--app-text-default);
  margin-top: 4px;
  opacity: 0.7;
  font-style: italic;
}
.hol-request-row__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}
.hol-requests__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  font-size: 0.85rem;
  color: var(--app-text-default);
  opacity: 0.6;
  gap: 4px;
}

/* ── Calendar legend ─────────────────────────────────────── */
.hol-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
}
.hol-legend__item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: var(--app-text-default);
}
.hol-legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.hol-legend__dot--approved {
  background-color: rgb(var(--v-theme-success));
}
.hol-legend__dot--pending {
  background-color: rgb(var(--v-theme-warning));
}
.hol-legend__dot--non-working {
  background-color: rgba(var(--v-theme-on-surface), 0.38);
}
.hol-legend__dot--short-day {
  background-color: rgb(var(--v-theme-info));
}

/* ── Calendar card & deep Vuetify overrides ──────────────── */
.calendar-card {
  display: flex;
  flex-direction: column;
  border-radius: var(--app-radius-base) !important;
}
.calendar-card :deep(.v-card-text) {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 auto;
  width: 100%;
}
.calendar-wrapper {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
}
.vacation-calendar {
  border-radius: var(--app-radius-base);
  flex: 1 1 auto;
  width: 100%;
  --v-theme-background: var(--v-theme-surface);
  --v-theme-surface-light: var(--v-theme-surface);
}
.calendar-card :deep(.v-date-picker) {
  width: 100%;
  max-width: 520px;
}
.calendar-card :deep(.v-calendar-month__days) {
  grid-auto-rows: 100px;
}
.calendar-card :deep(.v-calendar-month__day) {
  min-height: 50px;
  position: relative;
  border-radius: var(--app-radius-base);
  overflow: hidden;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.calendar-card :deep(.v-calendar-month__weeknumber) {
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.calendar-card :deep(.v-date-picker-month__day) {
  height: 50px;
  width: 52px;
}
.calendar-card :deep(.v-event) {
  white-space: normal;
  line-height: 1.2;
  padding: 2px 6px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.calendar-card :deep(.v-event-dot) {
  margin-top: 4px;
}
.calendar-card :deep(.v-event-title) {
  white-space: normal;
  overflow-wrap: anywhere;
}
.calendar-card :deep(.v-chip) {
  height: auto;
}
.calendar-card :deep(.v-chip__content) {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  white-space: normal;
  line-height: 1.25;
  word-break: break-word;
  font-size: 10px;
}
.calendar-card :deep(.v-calendar-weekly__day-label),
.calendar-card :deep(.v-calendar-weekly__day-content) {
  position: relative;
  z-index: 1;
}
.calendar-card :deep(.calendar-day-overlay) {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  border-radius: inherit;
}
.calendar-card :deep(.calendar-day-overlay--approved) {
  background-color: color-mix(in srgb, rgb(var(--v-theme-success)) 26%, transparent 74%);
}
.calendar-card :deep(.calendar-day-overlay--pending) {
  background-color: color-mix(in srgb, rgb(var(--v-theme-warning)) 28%, transparent 72%);
}
.calendar-card :deep(.calendar-day-overlay--non-working) {
  background-color: rgba(var(--v-theme-on-surface), 0.10);
}
.calendar-card :deep(.calendar-day-overlay--short-day) {
  background-color: color-mix(in srgb, rgb(var(--v-theme-info)) 32%, transparent 68%);
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--approved) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-success));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--pending) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-warning));
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--non-working) .v-calendar-weekly__day-label .v-btn__content) {
  color: var(--app-text-default);
  opacity: 0.55;
}
.calendar-card :deep(.v-calendar-month__day:has(.calendar-day-overlay--short-day) .v-calendar-weekly__day-label .v-btn__content) {
  color: rgb(var(--v-theme-info));
}
.calendar-card .v-date-picker {
  --v-date-picker-header-height: 64px;
  --v-date-picker-actions-display: none;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 960px) {
  .hol-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .hol-hero__actions {
    width: 100%;
  }
  .hol-balance-strip {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .hol-balance-strip {
    grid-template-columns: repeat(2, 1fr);
  }
  .hol-hero__name {
    font-size: 1.1rem;
  }
}


</style>
